import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { actions as homeActions } from "app/containers/Features/containers/Home/redux/slice";
import { ticketDetailsActions } from "app/containers/Features/containers/Ticket/redux/slice";
import { IEffortActual } from "app/containers/Features/containers/Ticket/types";
import { selectTimeSheetEntryDeleteLoading } from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import { ISelectedTicketDetails } from "app/containers/Features/containers/TimeSheet/types";
import { selectUserFormData } from "app/redux/selector";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTimeDuration } from "utils/hooks/useTimeTracker";

export const useEffortEntry = (
  entry: IEffortActual,
  selectedTicketDetails: ISelectedTicketDetails,
) => {
  // Hooks
  const dispatch = useDispatch();

  // Global States
  const timeSheetEntryDeleteLoading = useSelector(
    selectTimeSheetEntryDeleteLoading,
  );
  const projectMemberList = useSelector(selectProjectMemberList);
  const currentUser = useSelector(selectUserFormData);

  // Local States
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [localDate, setLocalDate] = useState(entry?.effort?.date);
  const [localStartTime, setLocalStartTime] = useState(
    entry?.effort?.startTime,
  );
  const [localEndTime, setLocalEndTime] = useState(entry?.effort?.endTime);

  // Effects
  useEffect(() => {
    setLocalDate(entry?.effort?.date);
    setLocalStartTime(entry?.effort?.startTime);
    setLocalEndTime(entry?.effort?.endTime);
  }, [entry]);

  const userId = projectMemberList.find(
    (m) => m._id === entry?.memberId,
  )?.userId;

  const isAssignee = currentUser._id === userId;

  const transplantTime = (originalISO: string, newDate: Date): string => {
    const src = new Date(originalISO);
    const dest = new Date(newDate);
    dest.setHours(
      src.getHours(),
      src.getMinutes(),
      src.getSeconds(),
      src.getMilliseconds(),
    );
    return dest.toISOString();
  };

  const updateEffort = (updates: {
    date?: string;
    startTime?: string;
    endTime?: string;
  }) => {
    const newDate = updates.date ?? localDate;
    const newStart = updates.startTime ?? localStartTime;
    const newEnd = updates.endTime ?? localEndTime;
    if (updates.date !== undefined) setLocalDate(newDate);
    if (updates.startTime !== undefined) setLocalStartTime(newStart);
    if (updates.endTime !== undefined) setLocalEndTime(newEnd);
    const duration = getTimeDuration(newStart, newEnd);

    dispatch(
      timeSheetActions.setTimeTrackerForm({
        selectedTicketDetails,
        date: newDate,
        startTime: newStart,
        endTime: newEnd,
        hours: duration.hours,
        minutes: duration.minutes,
        effortId: entry._id,
      }),
    );

    dispatch(
      timeSheetActions.effortActualRequest({
        callback({ form, response }) {
          dispatch(
            homeActions.updateEffortActualEntryLocally({
              ticketId: form?.selectedTicketDetails?._id,
              entryId: form?.effortId as string,
              updates: response,
            }),
          );
          dispatch(
            ticketDetailsActions.updateEffortActualEntryLocally({
              ticketId: form?.selectedTicketDetails?._id,
              entryId: form?.effortId as string,
              updates: response,
            }),
          );
        },
      }),
    );
  };

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;
    updateEffort({
      date: newDate.toISOString(),
      startTime: transplantTime(localStartTime, newDate),
      endTime: transplantTime(localEndTime, newDate),
    });
  };

  const handleDelete = () => {
    setSelectedEntryId(entry._id);
    dispatch(
      timeSheetActions.deleteTimeSheetEntryRequest({
        effortId: entry._id,
        callback: () => {
          dispatch(
            homeActions.deleteEffortActualEntryLocally({
              ticketId: selectedTicketDetails._id,
              effortId: entry._id,
            }),
          );
          dispatch(
            ticketDetailsActions.deleteEffortActualEntryLocally({
              effortId: entry._id,
            }),
          );
          setSelectedEntryId("");
        },
      }),
    );
  };

  return {
    dispatch,
    isAssignee,
    localDate,
    localStartTime,
    localEndTime,
    selectedEntryId,
    timeSheetEntryDeleteLoading,
    handleDateChange,
    handleStartTimeChange: (slot: { iso: string }) =>
      updateEffort({ startTime: slot.iso }),
    handleEndTimeChange: (slot: { iso: string }) =>
      updateEffort({ endTime: slot.iso }),
    handleDelete,
  };
};
